import React from 'react';
import { Helmet } from 'react-helmet-async';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';

const Section = ({ title, level = 2, children }) => (
  <Box sx={{ mt: level === 2 ? 4 : 3, mb: 1.5 }}>
    <Typography
      variant={level === 2 ? 'h5' : 'h6'}
      sx={{ fontWeight: 700, letterSpacing: '-0.01em', mb: 1 }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const P = ({ children }) => (
  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75, mb: 1.5 }}>
    {children}
  </Typography>
);

const UL = ({ children }) => (
  <Box component="ul" sx={{ pl: 3, color: 'text.secondary', mb: 2, '& li': { mb: 1, lineHeight: 1.7 } }}>
    {children}
  </Box>
);

function PrivacyPolicyScreen() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | AirCheck</title>
      </Helmet>
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            border: (t) => `1px solid ${t.palette.divider}`,
          }}
        >
          <Typography variant="overline" color="text.secondary">
            Last updated: July 26, 2024
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mb: 2 }}>
            Privacy Policy
          </Typography>
          <P>
            This Privacy Policy describes Our policies and procedures on the collection, use and
            disclosure of Your information when You use the Service and tells You about Your
            privacy rights and how the law protects You.
          </P>

          <Section title="Interpretation and Definitions">
            <Section title="Interpretation" level={3}>
              <P>
                The words of which the initial letter is capitalized have meanings defined under
                the following conditions. The following definitions shall have the same meaning
                regardless of whether they appear in singular or in plural.
              </P>
            </Section>
            <Section title="Definitions" level={3}>
              <P>For the purposes of this Privacy Policy:</P>
              <UL>
                <li><strong>Account</strong> means a unique account created for You to access our Service.</li>
                <li><strong>Affiliate</strong> means an entity that controls, is controlled by, or is under common control with a party.</li>
                <li><strong>Company</strong> (referred to as "the Company", "We", "Us" or "Our") refers to AirCheck.</li>
                <li><strong>Cookies</strong> are small files placed on Your device, containing details of Your browsing history.</li>
                <li><strong>Country</strong> refers to: Kenya.</li>
                <li><strong>Device</strong> means any device that can access the Service such as a computer, cellphone or tablet.</li>
                <li><strong>Personal Data</strong> is any information that relates to an identified or identifiable individual.</li>
                <li><strong>Service</strong> refers to the Website.</li>
                <li><strong>Service Provider</strong> means any natural or legal person who processes the data on behalf of the Company.</li>
                <li><strong>Usage Data</strong> refers to data collected automatically, either generated by the use of the Service or from the Service infrastructure itself.</li>
                <li>
                  <strong>Website</strong> refers to AirCheck, accessible from{' '}
                  <Link href="https://aircheck.kagwima.com" target="_blank" rel="external nofollow noopener">
                    aircheck.kagwima.com
                  </Link>
                </li>
                <li><strong>You</strong> means the individual accessing or using the Service.</li>
              </UL>
            </Section>
          </Section>

          <Section title="Collecting and Using Your Personal Data">
            <Section title="Types of Data Collected" level={3}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                Personal Data
              </Typography>
              <P>
                While using Our Service, We may ask You to provide Us with certain personally
                identifiable information, including but not limited to:
              </P>
              <UL>
                <li>Email address</li>
                <li>First name and last name</li>
                <li>Usage Data</li>
              </UL>

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                Usage Data
              </Typography>
              <P>
                Usage Data is collected automatically when using the Service and may include IP
                address, browser type, pages visited, time and date of visit, time spent on those
                pages, unique device identifiers, and other diagnostic data.
              </P>

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                Tracking Technologies and Cookies
              </Typography>
              <P>
                We use Cookies and similar tracking technologies to track the activity on Our
                Service and store certain information.
              </P>
            </Section>

            <Section title="Use of Your Personal Data" level={3}>
              <P>The Company may use Personal Data for the following purposes:</P>
              <UL>
                <li>To provide and maintain our Service.</li>
                <li>To manage Your Account.</li>
                <li>For the performance of a contract.</li>
                <li>To contact You.</li>
                <li>To provide You with news, special offers and general information.</li>
                <li>To manage Your requests.</li>
                <li>For business transfers.</li>
                <li>For other purposes such as data analysis and service improvement.</li>
              </UL>
            </Section>

            <Section title="Retention of Your Personal Data" level={3}>
              <P>
                The Company will retain Your Personal Data only for as long as is necessary for the
                purposes set out in this Privacy Policy.
              </P>
            </Section>

            <Section title="Delete Your Personal Data" level={3}>
              <P>
                You have the right to delete or request that We assist in deleting the Personal Data
                that We have collected about You. You may update, amend, or delete Your information
                at any time by signing in to Your Account.
              </P>
            </Section>

            <Section title="Security of Your Personal Data" level={3}>
              <P>
                The security of Your Personal Data is important to Us, but no method of transmission
                over the Internet, or method of electronic storage is 100% secure.
              </P>
            </Section>
          </Section>

          <Section title="Children's Privacy">
            <P>
              Our Service does not address anyone under the age of 13. We do not knowingly collect
              personally identifiable information from anyone under the age of 13.
            </P>
          </Section>

          <Section title="Links to Other Websites">
            <P>
              Our Service may contain links to other websites that are not operated by Us. We have
              no control over and assume no responsibility for the content, privacy policies, or
              practices of any third party sites or services.
            </P>
          </Section>

          <Section title="Changes to this Privacy Policy">
            <P>
              We may update Our Privacy Policy from time to time. We will notify You of any changes
              by posting the new Privacy Policy on this page.
            </P>
          </Section>

          <Section title="Contact Us">
            <P>
              If you have any questions about this Privacy Policy, you can contact us by email:{' '}
              <Link href="mailto:ekagwima745@gmail.com">ekagwima745@gmail.com</Link>.
            </P>
          </Section>
        </Paper>
      </Container>
    </>
  );
}

export default PrivacyPolicyScreen;
